import type { MatchSummary, Profile } from "../types";

const DATABASE = "turnstyle";
const VERSION = 1;
const PROFILE_STORE = "profile";
const MATCH_STORE = "matches";

const openDatabase = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE, VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(PROFILE_STORE)) database.createObjectStore(PROFILE_STORE);
      if (!database.objectStoreNames.contains(MATCH_STORE)) database.createObjectStore(MATCH_STORE, { keyPath: "roomId" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const requestResult = <T>(request: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionComplete = (transaction: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });

export async function getProfile(): Promise<Profile | null> {
  const database = await openDatabase();
  const transaction = database.transaction(PROFILE_STORE, "readonly");
  const result = await requestResult(transaction.objectStore(PROFILE_STORE).get("current"));
  database.close();
  return (result as Profile | undefined) ?? null;
}

export async function saveProfile(profile: Profile): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(PROFILE_STORE, "readwrite");
  transaction.objectStore(PROFILE_STORE).put(profile, "current");
  await transactionComplete(transaction);
  database.close();
}

export async function listMatches(): Promise<MatchSummary[]> {
  const database = await openDatabase();
  const transaction = database.transaction(MATCH_STORE, "readonly");
  const result = await requestResult(transaction.objectStore(MATCH_STORE).getAll());
  database.close();
  return (result as MatchSummary[]).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function saveMatch(match: MatchSummary): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(MATCH_STORE, "readwrite");
  transaction.objectStore(MATCH_STORE).put(match);
  await transactionComplete(transaction);
  database.close();
}

export async function deleteMatch(roomId: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(MATCH_STORE, "readwrite");
  transaction.objectStore(MATCH_STORE).delete(roomId);
  await transactionComplete(transaction);
  database.close();
}
